import { useDispatch, useSelector } from 'react-redux';
import cx from 'classnames';
import SVG from 'react-inlinesvg';
import infoSvg from 'assets/icons/info.svg';
import warningSvg from 'assets/icons/warning.svg';
import productSvg from 'assets/icons/product.svg';
import { hideBanner } from 'store/banner/reducer';
import { Store } from 'components/common/types/Store.types';
import Modal from 'lib/Modal';
import { useToggle } from 'customHooks/useToggle';
import { getDealId } from 'store/dealManagement/selectors/getDealId';
import IconButton from 'lib/IconButton';
import arrowLeftSvg from 'assets/icons/arrow_left.svg';
import { ButtonSize, Theme } from 'lib/IconButton/IconButton.types';
import Chip from '../Chip';
import Button, { Color } from '../Button';
import {
  backgroundColorStyle,
  BannerType,
  confirmButtonBackgroundColorStyle,
  confirmButtonStyle,
  containerBackgroundColorStyle,
  fontColorStyle,
} from './Banner.types';

const iconSvgMap: { [key in Exclude<BannerType, BannerType.NONE>]: string } = {
  [BannerType.INFO]: infoSvg,
  [BannerType.WARNING]: warningSvg,
  [BannerType.ERROR]: warningSvg,
  [BannerType.INFO_ALT]: productSvg,
};

type DiscardConfirmationModalProps = {
  isOpen: boolean;
  onClose: () => void;
  onConfirm: () => void;
};

const DiscardConfirmationModal: React.FC<DiscardConfirmationModalProps> = ({ isOpen, onClose, onConfirm }) => (
  <Modal
    title="Discard changes?"
    text="Changes made will be permanently discarded."
    isOpen={isOpen}
    onClose={onClose}
    icon={warningSvg}
    actionButtons={
      <>
        <Button label="Cancel" color={Color.SECONDARY} onClick={onClose} />
        <Button label="Confirm discard" color={Color.DANGER} onClick={onConfirm} />
      </>
    }
  />
);

export const Banner: React.FC = () => {
  const dispatch = useDispatch();
  const bannerType = useSelector((state: Store) => state.banner?.type);
  const headerInfo = useSelector((state: Store) => state.banner?.headerInfo);
  const contentInfo = useSelector((state: Store) => state.banner?.contentInfo);
  const backButton = useSelector((state: Store) => state.banner?.backButton);
  const discardButton = useSelector((state: Store) => state.banner?.discardButton);
  const confirmButton = useSelector((state: Store) => state.banner?.confirmButton);
  const exitButton = useSelector((state: Store) => state.banner?.exitButton);
  const chip = useSelector((state: Store) => state.banner?.chip);
  const campaignId = useSelector(getDealId);

  const { isOpen: isDiscardModalOpen, open: openDiscardModal, close: closeDiscardModal } = useToggle();
  const { isOpen: isBackModalOpen, open: openBackModal, close: closeBackModal } = useToggle();

  if (bannerType === BannerType.NONE) return null;

  const onBackButtonClick = (): void => {
    if (backButton?.isChange) {
      openBackModal();
      return;
    }

    backButton?.actionCallback?.();
  };

  const handleDiscardConfirm = (): void => {
    discardButton?.actionCallback?.(campaignId);
    closeDiscardModal();
  };

  const handleBackConfirm = (): void => {
    backButton?.actionCallback?.();
    closeBackModal();
  };

  const handleExit = (): void => {
    exitButton?.actionCallback?.();
    dispatch(hideBanner());
  };

  const handleConfirm = (): void => confirmButton?.actionCallback?.(campaignId);

  return (
    <div
      className={cx(
        'w-full body-base pt-0.5 pb-[8px] px-2 flex items-center',
        fontColorStyle[bannerType],
        backgroundColorStyle[bannerType],
      )}
    >
      {backButton && (
        <span className="mr-4">
          <IconButton
            buttonSize={ButtonSize.SMALL}
            icon={arrowLeftSvg}
            dataTestId="back-click"
            handleOnClick={onBackButtonClick}
            name="back-click"
            theme={Theme.PRIMARY}
          />
        </span>
      )}
      <div
        className={cx('flex flex-row space-x-2 items-center py-0.5 px-1.5', containerBackgroundColorStyle[bannerType])}
      >
        <div className="flex items-start justify-center shrink-0">
          <SVG src={iconSvgMap[bannerType]} className="fill-current" />
        </div>
        <div className="flex flex-row items-center space-x-4">
          <div className="flex flex-row items-center gap-2">
            {headerInfo && <span className="sub-header-base">{headerInfo}</span>}
          </div>
          {contentInfo && <span className="body-base">{contentInfo}</span>}

          <div className="flex flex-row space-x-4">
            {discardButton && (
              <Button
                label={discardButton.buttonLabel ?? 'Discard'}
                color={Color.BLACK_AND_WHITE}
                onClick={openDiscardModal}
                classNames="!bg-transparent !p-0 border-none hover:bg-transparent"
              />
            )}
            <DiscardConfirmationModal
              isOpen={isDiscardModalOpen}
              onClose={closeDiscardModal}
              onConfirm={handleDiscardConfirm}
            />
            <DiscardConfirmationModal isOpen={isBackModalOpen} onClose={closeBackModal} onConfirm={handleBackConfirm} />
            {exitButton && (
              <Button
                label={exitButton.buttonLabel ?? 'Exit'}
                color={Color.BLACK_AND_WHITE}
                onClick={handleExit}
                classNames="!bg-transparent !p-0 border-none hover:bg-transparent"
              />
            )}
            {confirmButton && (
              <Button
                label={confirmButton.buttonLabel ?? 'Confirm'}
                color={confirmButtonBackgroundColorStyle[bannerType]}
                onClick={handleConfirm}
                classNames={confirmButtonStyle[bannerType]}
                isDisabled={confirmButton?.isDisabled}
              />
            )}
          </div>
        </div>
      </div>
      {chip && (
        <div className="ml-1 flex items-center">
          <Chip label={chip.label} color={chip.color} truncateLabel={chip.truncateLabel ?? false} />
        </div>
      )}
    </div>
  );
};
